import { FC, useState, useMemo, useCallback, useEffect, useContext } from 'react';
import cn from 'classnames';
import { Button, TextInput } from '@datassential/platform-ui-lib';
import { FiSearch } from "react-icons/fi";
import { CSSTransition } from 'react-transition-group';
import { Link } from 'react-router-dom';
import ToggleInput from '@components/ToggleInput/ToggleInput';
import LowPenetrationDropdown from '@components/LowPenetrationDropdown/LowPenetrationDropdown';
import Filters from '@components/Filters/Filters';
import SelectFilters from '@vms/SelectFilters';
import KeywordsList from '@components/KeywordsList/KeywordsList';
import { IconFiltersFunnel } from '@icons';
import { makeEnding } from '@core/old_helpers';
import { useDebouncedCallback } from '@hooks/useDebouncedCallback';
import { GlobalContext } from '@components/App/App.context';
import { useTopTrends } from './TopTrendsContext';
import { Routes } from "@core/routes";

export type CategoryItem = {
    id: number;
    name: string;
    isSelected: boolean;
};

const TopTrendsControls: FC = () => {
    const { getRem } = useContext(GlobalContext);
    const {
        searchQuery,
        onSearchChange,
        isSelectable,
        onSelectableToggle,
        selectedWords,
        onRemoveSelectedWord,
        onClearSelections,
        showOnlyNewItems,
        onNewItemsToggle,
        hasNewItemsPermission,
        includeLowPenetrationItems,
        includeLessTenMenusCount,
        onIncludeOptionsChange,
        categories,
        onCategoriesApply,
        onCategoriesClear,
    } = useTopTrends();
    const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);
    const [isCategoriesPopupOpened, setIsCategoriesPopupOpened] = useState(false);
    const [localCategories, setLocalCategories] = useState<CategoryItem[]>([]);
    const [tooltipShownInx, setTooltipShownInx] = useState<number | null | undefined>();

    const handleTooltipChange = useCallback((value: number | null | undefined) => {
        setTooltipShownInx(value);
    }, []);

    useEffect(() => {
        setLocalSearchQuery(searchQuery);
    }, [searchQuery]);

    useEffect(() => {
        setLocalCategories(categories);
    }, [categories]);

    const debouncedSearch = useDebouncedCallback((value: string) => {
        onSearchChange(value);
    }, 300);

    const handleSearchChange = useCallback((value: string) => {
        setLocalSearchQuery(value);
        debouncedSearch(value);
    }, [debouncedSearch]);

    const isCategoriesApplied = useMemo(
        () => categories.some(c => c.isSelected),
        [categories]
    );

    const appliedCategoriesCount = useMemo(
        () => categories.filter(c => c.isSelected).length,
        [categories]
    );

    const isApplyButtonHighlighted = useMemo(() => {
        if (localCategories.length === 0) return false;
        return categories.some(
            category => {
                const localCat = localCategories.find(({ id }) => id === category.id);
                return localCat && localCat.isSelected !== category.isSelected;
            }
        ) && localCategories.some(category => category.isSelected);
    }, [localCategories, categories]);

    const toggleCategory = useCallback((id: number) => {
        setLocalCategories(prev => prev.map(category => ({
            ...category,
            isSelected: id === category.id ? !category.isSelected : category.isSelected,
        })));
    }, []);

    const handleApplyCategories = useCallback(() => {
        const selectedIds = localCategories
            .filter(({ isSelected }) => isSelected)
            .map(({ id }) => id);
        onCategoriesApply(selectedIds);
        setIsCategoriesPopupOpened(false);
    }, [localCategories, onCategoriesApply]);

    const handleClearCategories = useCallback(() => {
        onCategoriesClear();
        setIsCategoriesPopupOpened(false);
    }, [onCategoriesClear]);

    const showSelectedWordsUi = isSelectable && selectedWords.length > 0;
    const linkToByo = selectedWords.join(',');

    return (
        <div className="TopTrendsControls">
            <div className="TopTrendsControls__left">
                <div className="TopTrendsControls__search">
                    <TextInput
                        value={localSearchQuery}
                        setValue={handleSearchChange}
                        placeholder="Search for a keyword"
                    >
                        <FiSearch className="TopTrendsControls__search-icon" />
                    </TextInput>
                </div>

                <ToggleInput
                    className="TopTrendsControls__toggle"
                    labelText="Build keyword list"
                    isChecked={isSelectable}
                    onToggle={onSelectableToggle}
                />

                <ToggleInput
                    isVisible={hasNewItemsPermission}
                    className="TopTrendsControls__toggle"
                    labelText="New items only:"
                    isChecked={showOnlyNewItems}
                    onToggle={onNewItemsToggle}
                    tooltipLabel="Only include items that are new to menus"
                    tooltipShownInx={tooltipShownInx ?? undefined}
                    tooltipInx={1}
                    // @ts-expect-error - ToggleInput is JS without proper types, but accepts (value: number | null) => void
                    setTooltipShownInx={handleTooltipChange}
                />

                <LowPenetrationDropdown
                    includeLowPenetrationItems={includeLowPenetrationItems}
                    includeLessTenMenusCount={includeLessTenMenusCount}
                    setIncludeOptions={onIncludeOptionsChange}
                />
            </div>

            <div className="TopTrendsControls__right">
                {showSelectedWordsUi ? (
                    <>
                        <button
                            className="TopTrendsControls__clear-btn"
                            onClick={onClearSelections}
                        >
                            CLEAR SELECTIONS
                        </button>
                        <div className="TopTrendsControls__selected-info">
                            <div>
                                {selectedWords.length} keywords selected
                            </div>
                            <KeywordsList
                                height={selectedWords.length >= 8 ? `${getRem(255)}` : getRem(32 * selectedWords.length)}
                                withoutScrollBar={selectedWords.length < 8}
                                removeKeyword={onRemoveSelectedWord}
                                className="TopTrendsControls__selected-keywords-list"
                                keywords={selectedWords}
                            />
                        </div>
                        <Link
                            to={`${Routes.byo}?words=${linkToByo}`}
                            className={cn(
                                'TopTrendsControls__go-button',
                                selectedWords.length === 0 && 'TopTrendsControls__go-button--disabled',
                            )}
                        >
                            Go
                        </Link>
                    </>
                ) : (
                    <>
                        {isCategoriesApplied && (
                            <button
                                className="TopTrendsControls__clear-btn"
                                onClick={handleClearCategories}
                            >
                                CLEAR CATEGORIES
                            </button>
                        )}

                        <Button
                            className={cn(
                                'TopTrendsControls__category-button',
                                'Indicatable',
                                isCategoriesApplied && 'is-applied',
                            )}
                            onClick={() => setIsCategoriesPopupOpened(!isCategoriesPopupOpened)}
                        >
                            <IconFiltersFunnel />
                            {isCategoriesApplied
                                ? `${appliedCategoriesCount === categories.length ? 'All' : appliedCategoriesCount} ${makeEnding('Category', appliedCategoriesCount)} Applied`
                                : 'Narrow by category'}
                        </Button>

                        <CSSTransition
                            in={isCategoriesPopupOpened}
                            classNames="appears"
                            timeout={300}
                            unmountOnExit
                        >
                            <div className="TopTrendsControls__categories-popup">
                                <Filters
                                    filtersArray={localCategories}
                                    toggleFilter={toggleCategory}
                                    // @ts-ignore this is a JSX component so ignore TS error due to lacking types for it
                                    applyFilters={handleApplyCategories}
                                    isApplyButtonHighlighted={isApplyButtonHighlighted}
                                />
                            </div>
                        </CSSTransition>

                        <SelectFilters testId="buttonTopTrendsFilters" />
                    </>
                )}
            </div>
        </div>
    );
};

export default TopTrendsControls;
